import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import toast from "react-hot-toast";
import { useAxios } from "../customHooks";
import { useLocation } from "react-router-dom";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const {pathname} = useLocation()
  const { response, isLoading, operation } = useAxios();
  const { response: responseUpdateUserProfile, operation: updateUserProfile } =
    useAxios();
  const {response: currentUserResponse, operation: getCurrentUserOperation} = useAxios()
  const {response: channelProfileResponse, operation: getChannelProfileOperation} = useAxios()
  const [editUserData, setEditUserData] = useState({
    email: "",
    fullName: "",
    avatar: "",
    coverImage: "",
  });
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupUser, setSignUpUser] = useState({
    fullName: "",
    username: "",
    email: "",
    password: "",
    avatar: "",
    coverImage: "",
  });
  const [user, setUser] = useState(null);
  const [channelProfile, setChannelProfile] = useState({
    channel: {},
    videosByUser: [],
  });
  const [encodedToken, setEncodedToken] = useState(null);
  const [currentUser, setCurrentUser] = useState({
    user: {},
    videosByCurrentUser: []
  })

  useEffect(() => {
    let localToken = localStorage.getItem("myToken");
    let localUser = JSON.parse(localStorage.getItem("user"));
    if (localToken !== null) {
      setEncodedToken(localToken);
    }
    if (localUser !== null) {
      setUser(localUser);
    }
  }, []);

  useEffect(() =>{
    if(encodedToken){
      getCurrentUserOperation({
        method: 'get',
        url:`${process.env.REACT_APP_BACKEND_API}users/get-user`,
        headers: { authorization: encodedToken },
      }) 
    }
  },[encodedToken,responseUpdateUserProfile])

 useEffect(() => {
  if(currentUserResponse){
    setCurrentUser((prev) => ({
      ...prev,
      videosByCurrentUser: currentUserResponse.data.videosByCurrentUser,
      user: currentUserResponse.data.user
    }))
  }
 },[currentUserResponse])

  useEffect(() => {
    if(pathname.includes('/profile/')){
      getChannelProfileOperation({
        method: "get",
        url: `${process.env.REACT_APP_BACKEND_API}users/c/${pathname.replace("/profile/@", "")}`,
        headers: { authorization: encodedToken },
      })
    }
  }, [pathname]);

  const handleLogin = (e) => {
    e.preventDefault();
    operation({
      method: "post",
      url: `${process.env.REACT_APP_BACKEND_API}users/login`,
      data: { email: loginData.email, password: loginData.password },
    });
    setLoginData({ email: "", password: "" });
  };

  const updateAvatar = () => {
    const formData = new FormData();
    formData.append("avatar", editUserData.avatar);
    if (editUserData.avatar) {
      updateUserProfile({
        method: "patch",
        url: `${process.env.REACT_APP_BACKEND_API}users/update-avatar`,
        data: formData,
        headers: { authorization: encodedToken },
      });
      setEditUserData((prev) => ({ ...prev, avatar: "" }));
    }
  };

  const updateCoverImage = () => {
    const formData = new FormData();
    formData.append("coverImage", editUserData.coverImage);
    if (editUserData.coverImage) {
      updateUserProfile({
        method: "patch",
        url: `${process.env.REACT_APP_BACKEND_API}users/update-coverImage`,
        data: formData,
        headers: { authorization: encodedToken },
      });
      setEditUserData((prev) => ({ ...prev, coverImage: "" }));
    }
  };

  const updateAccountDetails = () => {
    if (editUserData.email || editUserData.fullName) {
      updateUserProfile({
        method: "patch",
        url: `${process.env.REACT_APP_BACKEND_API}users/update-account-details`,
        data: { email: editUserData.email, fullName: editUserData.fullName },
        headers: { authorization: encodedToken },
      });
      setEditUserData((prev) => ({
        ...prev,
        email: "",
        fullName: "",
      }));
    }
  };

  const handleUpdate = () => {
    updateAvatar();
    updateCoverImage();
    updateAccountDetails();
  };

  const signupHandler = (e) => {
    const formData = new FormData();
    const userDataArray = Object.keys(signupUser);
    for (const element of userDataArray) {
      formData.append(element.toString(), signupUser[element]);
    }
    e.preventDefault();
    operation({
      method: "post",
      url: `${process.env.REACT_APP_BACKEND_API}users/register`,
      data: formData,
    });
    setSignUpUser({
      fullName: "",
      userName: "",
      email: "",
      password: "",
      avatar: "",
      coverImage: "",
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    setEncodedToken(null);
    setUser(null);
    toast.success("Logged out .", { duration: 1000 });
  };

  useEffect(() => {
    if (response !== undefined) {
      localStorage.setItem("myToken", response.data.accessToken, 864);
      if (response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        setUser(response.data.user);
      }
      setEncodedToken(response.data.accessToken);
    }
  }, [response]);

  useEffect(() => {
    if (channelProfileResponse) {
      setChannelProfile(() => ({
        channel: channelProfileResponse.data.channel,
        videosByUser: channelProfileResponse.data.videosByUser,
      }));
    }
  }, [channelProfileResponse]);

  useEffect(() => {
    if (responseUpdateUserProfile) {
      setCurrentUser((prev) => ({
        ...prev,
        user: responseUpdateUserProfile.data,
      }));
    }
  }, [responseUpdateUserProfile]);

  const memoizedContextValue = useMemo(
    () => ({
      encodedToken,
      isLoading,
      handleLogin,
      setLoginData,
      loginData,
      handleLogout,
      user,
      setSignUpUser,
      signupUser,
      signupHandler,
      channelProfile,
      editUserData,
      setEditUserData,
      handleUpdate,
      currentUser,
      getCurrentUserOperation,
      responseUpdateUserProfile
    }),
    [
      encodedToken,
      isLoading,
      handleLogin,
      setLoginData,
      loginData,
      handleLogout,
      user,
      setSignUpUser,
      signupUser,
      signupHandler,
      channelProfile,
      editUserData,
      setEditUserData,
      handleUpdate,
      currentUser,
      getCurrentUserOperation,
      responseUpdateUserProfile
    ]
  );
  return (
    <AuthContext.Provider value={memoizedContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
